import { z } from "zod";
import {
  isoDateTimeString,
  pagePaginationQuerySchema,
  pagedResponseSchema,
  phoneSchema,
  uuidSchema,
} from "./common";
import {
  accountTypeSchema,
  globalRoleSchema,
  userMembershipSchema,
  userStatusSchema,
} from "./users";
import {
  orgKycSubmissionAdminSchema,
  workerKycSubmissionAdminSchema,
} from "./kyc";
import { orgSchema } from "./orgs";
import { subscriptionPlanSchema, orgPlanStatusSchema } from "./subscriptions";
import { PROVINCE_CODES, DEFAULT_BUSINESS_TYPE, PLAN_CODES } from "../catalogs";
import { postingSchema, postingStateSchema } from "./postings";
import { userMeSchema } from "./users";
import { inviteStatusSchema, inviteContactSchema } from "./invites";
import { applicationSourceSchema } from "./applications";

// ============================================================================
// Dashboard
// ============================================================================
export const adminDashboardResponseSchema = z.object({
  today: z.object({
    newUsers: z.number().int(),
    newPostings: z.number().int(),
    invitesCommitted: z.number().int(),
    creditPurchaseAmountVnd: z.string(),
    workerKycApproved: z.number().int(),
    orgKycApproved: z.number().int(),
    abuseReportsOpened: z.number().int(),
  }),
  totals: z.object({
    users: z.number().int(),
    activeOrgs: z.number().int(),
    openPostings: z.number().int(),
  }),
});

// ============================================================================
// Analytics (admin chart dashboard)
// ============================================================================
// Every endpoint accepts an optional [from, to) UTC window (default: last 30
// days). Daily buckets are computed in Asia/Ho_Chi_Minh on the server, so a row
// created at 23:30 UTC lands on the next VN day.
export const adminAnalyticsRangeQuerySchema = z.object({
  from: isoDateTimeString.optional(),
  to: isoDateTimeString.optional(),
});

// One KPI: current-period value, the previous equal-length period value, and
// the percent change (null when prevValue is 0 — no baseline to compare).
export const adminAnalyticsMetricSchema = z.object({
  value: z.number(),
  prevValue: z.number(),
  deltaPct: z.number().nullable(),
});

export const adminAnalyticsSummarySchema = z.object({
  rangeDays: z.number().int(),
  newUsers: adminAnalyticsMetricSchema,
  newPostings: adminAnalyticsMetricSchema,
  applications: adminAnalyticsMetricSchema,
  invitesCommitted: adminAnalyticsMetricSchema,
  revenueVnd: adminAnalyticsMetricSchema,
  payingOrgs: adminAnalyticsMetricSchema,
  // value is a 0..1 ratio (PAID / total purchases in the window).
  paymentSuccessRate: adminAnalyticsMetricSchema,
});

export const adminAnalyticsTimeseriesPointSchema = z.object({
  date: z.string(), // YYYY-MM-DD in Asia/Ho_Chi_Minh
  newWorkers: z.number().int(),
  newEmployers: z.number().int(),
  newOrgs: z.number().int(),
  newOrgsShadow: z.number().int(),
  newPostings: z.number().int(),
  newPostingsImported: z.number().int(),
  applications: z.number().int(),
  applicationsSourced: z.number().int(),
  invitesCommitted: z.number().int(),
  revenueVnd: z.number(),
});

export const adminAnalyticsTimeseriesSchema = z.object({
  points: z.array(adminAnalyticsTimeseriesPointSchema),
});

export const adminAnalyticsFunnelSchema = z.object({
  // Each stage = distinct applications that reached it within the window
  // (counted from ApplicationEvent so progression is cumulative, not a snapshot
  // of the current state).
  application: z.object({
    applied: z.number().int(),
    invited: z.number().int(),
    accepted: z.number().int(),
    committed: z.number().int(),
    rejected: z.number().int(),
    withdrawn: z.number().int(),
    expired: z.number().int(),
  }),
  inviteStatus: z.object({
    LOCKED: z.number().int(),
    ACCEPTED: z.number().int(),
    COMMITTED: z.number().int(),
    REJECTED: z.number().int(),
    EXPIRED: z.number().int(),
    CANCELLED: z.number().int(),
  }),
  // COMMITTED invites / total invites created in the window.
  commitRate: z.number(),
  postings: z.object({
    created: z.number().int(),
    // Postings created in the window that got at least one COMMITTED invite.
    filled: z.number().int(),
    fillRate: z.number(),
  }),
});

export const adminAnalyticsRevenueSchema = z.object({
  series: z.array(
    z.object({
      date: z.string(),
      revenueVnd: z.number(),
    }),
  ),
  // Revenue grouped by what was bought: a plan (PRO/BUSINESS) or an à-la-carte
  // top-up (Lượt săn / Lượt boost). `label` is display-ready.
  byPack: z.array(
    z.object({
      label: z.string(),
      revenueVnd: z.number(),
      orders: z.number().int(),
    }),
  ),
  statusCounts: z.object({
    PENDING: z.number().int(),
    PAID: z.number().int(),
    EXPIRED: z.number().int(),
    FAILED: z.number().int(),
    total: z.number().int(),
  }),
  paymentSuccessRate: z.number(),
  payingOrgs: z.number().int(),
});

// ============================================================================
// Users (admin list)
// ============================================================================
export const adminUserItemSchema = z.object({
  id: uuidSchema,
  phone: z.string().nullable(),
  email: z.string().nullable(),
  globalRole: globalRoleSchema,
  accountType: accountTypeSchema,
  status: userStatusSchema,
  displayName: z.string().nullable(),
  workerKycVerifiedAt: isoDateTimeString.nullable(),
  memberships: z.array(userMembershipSchema),
  createdAt: isoDateTimeString,
});

export const adminUsersQuerySchema = pagePaginationQuerySchema.extend({
  accountType: accountTypeSchema.optional(),
  globalRole: globalRoleSchema.optional(),
  status: userStatusSchema.optional(),
  search: z.string().optional(),
});

export const adminUsersListResponseSchema =
  pagedResponseSchema(adminUserItemSchema);

export const adminSuspendUserRequestSchema = z.object({
  reason: z.string().max(2000).optional(),
});

export const adminUserMutationResponseSchema = z.object({
  userId: uuidSchema,
  status: userStatusSchema,
  updatedAt: isoDateTimeString,
});

/** Admin contact lookup — privileged action, audit-logged. */
export const adminUserContactResponseSchema = z.object({
  userId: uuidSchema,
  phone: z.string().nullable(),
  accessedAt: isoDateTimeString,
});

// ============================================================================
// KYC review (worker + org)
// ============================================================================
export const adminKycListQuerySchema = pagePaginationQuerySchema.extend({
  status: z
    .enum(["NONE", "PENDING", "APPROVED", "REJECTED", "EXPIRED"])
    .optional(),
});

export const adminWorkerKycListResponseSchema = pagedResponseSchema(
  workerKycSubmissionAdminSchema,
);

export const adminOrgKycListResponseSchema = pagedResponseSchema(
  orgKycSubmissionAdminSchema,
);

// ============================================================================
// Orgs (admin)
// ============================================================================
export const adminOrgListQuerySchema = pagePaginationQuerySchema.extend({
  kycStatus: z
    .enum(["NONE", "PENDING", "APPROVED", "REJECTED", "EXPIRED"])
    .optional(),
  search: z.string().optional(),
  // Active = not soft-deleted (default). Deactivated = soft-deleted orgs only.
  status: z.enum(["active", "deactivated"]).optional(),
});

export const adminOrgListItemSchema = orgSchema.extend({
  ownerPhone: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  postingCount: z.number().int(),
  // Soft-delete timestamp; non-null = deactivated. Drives the toggle action.
  deactivatedAt: z.string().nullable(),
});

export const adminOrgListResponseSchema = pagedResponseSchema(
  adminOrgListItemSchema,
);

// ============================================================================
// Interview credit purchases (admin view)
// ============================================================================
export const creditPurchaseStatusSchema = z.enum([
  "PENDING",
  "PAID",
  "EXPIRED",
  "FAILED",
]);

export const adminCreditPurchaseListQuerySchema =
  pagePaginationQuerySchema.extend({
    status: creditPurchaseStatusSchema.optional(),
    orgId: uuidSchema.optional(),
    from: isoDateTimeString.optional(),
    to: isoDateTimeString.optional(),
    search: z.string().trim().min(1).max(120).optional(),
  });

export const adminCreditPurchaseItemSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  orgName: z.string(),
  packCode: z.string(),
  amountVnd: z.string(),
  creditCount: z.number().int(),
  status: creditPurchaseStatusSchema,
  provider: z.enum(["SEPAY"]),
  providerOrderId: z.string(),
  paidAt: isoDateTimeString.nullable().optional(),
  createdAt: isoDateTimeString,
});

export const adminCreditPurchaseListResponseSchema = pagedResponseSchema(
  adminCreditPurchaseItemSchema,
);

// Cash-flow snapshot for the finance dashboard cards. Filtered by the same
// window as the list (status/org/date/search).
export const adminCreditPurchaseSummaryQuerySchema = z.object({
  orgId: uuidSchema.optional(),
  from: isoDateTimeString.optional(),
  to: isoDateTimeString.optional(),
  search: z.string().trim().min(1).max(120).optional(),
});

export const adminCreditPurchaseSummarySchema = z.object({
  paidAmountVnd: z.string(),
  creditsSold: z.number().int(),
  counts: z.object({
    PENDING: z.number().int(),
    PAID: z.number().int(),
    EXPIRED: z.number().int(),
    FAILED: z.number().int(),
    total: z.number().int(),
  }),
  successRate: z.number(),
});

// ============================================================================
// Subscription plans + plan purchases (admin) — replaces the credit economy
// ============================================================================

// Admin upsert for a subscription plan (by `code`). Mirrors the runtime
// SubscriptionPlan shape minus server-managed fields.
export const adminSubscriptionPlanUpsertRequestSchema = z.object({
  code: z.enum(PLAN_CODES),
  name: z.string().min(1).max(80),
  priceVnd: z.number().int().nonnegative(),
  // Optional struck-through marketing price (null/omit = none).
  originalPriceVnd: z.number().int().nonnegative().nullable().optional(),
  durationDays: z.number().int().nonnegative(),
  sourcingQuota: z.number().int().nonnegative(),
  maxConcurrentPostings: z.number().int().nonnegative(),
  boostQuota: z.number().int().nonnegative(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});
export type AdminSubscriptionPlanUpsertRequest = z.infer<
  typeof adminSubscriptionPlanUpsertRequestSchema
>;

export const adminSubscriptionPlanListResponseSchema = z.object({
  items: z.array(subscriptionPlanSchema),
});

// A plan purchase row for the admin finance list.
export const adminPlanPurchaseItemSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  orgName: z.string(),
  // What the order buys: a plan, or an à-la-carte top-up (sourcing/boost).
  kind: z.enum(["PLAN", "SOURCING", "BOOST"]),
  // Set only for PLAN orders; null for top-ups.
  planCode: z.enum(PLAN_CODES).nullable(),
  // Units bought for a top-up (SOURCING/BOOST); null for plans.
  quantity: z.number().int().nullable(),
  amountVnd: z.string(),
  status: creditPurchaseStatusSchema, // same PENDING/PAID/EXPIRED/FAILED enum
  // Plan/top-up orders migrated to CASSO; older orders may be SEPAY or null
  // (dev/simulated). Must list every provider the DB enum allows or zod rejects
  // the whole list and the admin table shows "no orders".
  provider: z.enum(["SEPAY", "CASSO"]).nullable(),
  providerOrderId: z.string(),
  paidAt: isoDateTimeString.nullable().optional(),
  createdAt: isoDateTimeString,
});
export type AdminPlanPurchaseItem = z.infer<typeof adminPlanPurchaseItemSchema>;

export const adminPlanPurchaseListQuerySchema =
  pagePaginationQuerySchema.extend({
    status: creditPurchaseStatusSchema.optional(),
    orgId: uuidSchema.optional(),
    from: isoDateTimeString.optional(),
    to: isoDateTimeString.optional(),
    search: z.string().trim().min(1).max(120).optional(),
  });
export type AdminPlanPurchaseListQuery = z.infer<
  typeof adminPlanPurchaseListQuerySchema
>;

export const adminPlanPurchaseListResponseSchema = pagedResponseSchema(
  adminPlanPurchaseItemSchema,
);

// Dashboard subscription stats (M2).
export const adminSubscriptionStatsSchema = z.object({
  totalOrgs: z.number().int(),
  activeByPlan: z.object({
    FREE: z.number().int(),
    PRO: z.number().int(),
    BUSINESS: z.number().int(),
  }),
  // Provenance of the ACTIVE-Pro population by how the current plan was last
  // activated (subscription.sourceKind, last-activation-wins). Disjoint —
  // gift + purchase = activeByPlan.PRO.
  proBySource: z.object({
    gift: z.number().int(),
    purchase: z.number().int(),
  }),
  paidRevenueVnd: z.string(),
  paidPurchaseCount: z.number().int(),
  pendingPurchaseCount: z.number().int(),
});

// The (i) drill-down behind a "Pro từ mã / từ mua" stat: the orgs counted.
export const adminProSourceOrgSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  // Current Pro expiry (null = no expiry, which for Pro shouldn't happen but is
  // allowed by the model).
  expiresAt: isoDateTimeString.nullable(),
});
export type AdminProSourceOrg = z.infer<typeof adminProSourceOrgSchema>;

export const adminProSourceOrgsQuerySchema = z.object({
  source: z.enum(["GIFT", "PURCHASE"]),
});
export type AdminProSourceOrgsQuery = z.infer<
  typeof adminProSourceOrgsQuerySchema
>;

export const adminProSourceOrgsResponseSchema = z.object({
  items: z.array(adminProSourceOrgSchema),
});
export type AdminProSourceOrgsResponse = z.infer<
  typeof adminProSourceOrgsResponseSchema
>;

// Concierge: admin grants/renews an org's plan directly (no payment). Returns
// the updated plan status.
export const adminSubscribeOrgRequestSchema = z.object({
  planCode: z.enum(PLAN_CODES),
});
export const adminOrgPlanStatusResponseSchema = orgPlanStatusSchema;

export const adminCreditPurchaseLedgerSchema = z.object({
  id: uuidSchema,
  delta: z.number().int(),
  reason: z.string(),
  balanceAfter: z.number().int(),
  note: z.string().nullable(),
  createdAt: isoDateTimeString,
});

export const adminCreditPurchaseWebhookEventSchema = z.object({
  id: uuidSchema,
  eventId: z.string(),
  processedAt: isoDateTimeString.nullable(),
  createdAt: isoDateTimeString,
  rawPayload: z.unknown().nullable(),
});

export const adminCreditPurchaseDetailSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  orgName: z.string(),
  purchasedByEmail: z.string().nullable(),
  packCode: z.string(),
  amountVnd: z.string(),
  unitPriceVnd: z.string(),
  creditCount: z.number().int(),
  status: creditPurchaseStatusSchema,
  provider: z.enum(["SEPAY"]),
  providerOrderId: z.string(),
  providerCheckoutUrl: z.string().nullable(),
  providerPaymentId: z.string().nullable(),
  providerMetadata: z.unknown().nullable(),
  deadlineAt: isoDateTimeString,
  paidAt: isoDateTimeString.nullable(),
  creditedAt: isoDateTimeString.nullable(),
  createdAt: isoDateTimeString,
  ledger: adminCreditPurchaseLedgerSchema.nullable(),
  webhookEvents: z.array(adminCreditPurchaseWebhookEventSchema),
});

export const adminCreditPurchaseMarkPaidRequestSchema = z.object({
  note: z.string().trim().min(1).max(500),
});

export const adminCreditPurchaseActionResponseSchema = z.object({
  providerStatus: z.string(),
  creditedNow: z.boolean(),
  detail: adminCreditPurchaseDetailSchema,
});

// ============================================================================
// Contacts / invites (admin cross-org "Liên hệ" list)
// ============================================================================
// One row = an Invite — the unit the product calls a "liên hệ". This is the
// ops drill-down behind the dashboard's aggregate invite-status chart: browse
// every contact across all orgs, filter by lifecycle status (đã chốt / đã mời /
// từ chối / hết hạn / đã hủy), and read the shared contact snapshot once an
// invite is COMMITTED. Window is on `createdAt` (when the invite was issued).
export const adminInviteListQuerySchema = pagePaginationQuerySchema.extend({
  status: inviteStatusSchema.optional(),
  orgId: uuidSchema.optional(),
  from: isoDateTimeString.optional(),
  to: isoDateTimeString.optional(),
  // Matches org name, posting title, or worker full name.
  search: z.string().trim().min(1).max(120).optional(),
});

export const adminInviteItemSchema = z.object({
  id: uuidSchema,
  status: inviteStatusSchema,
  // Worker-applied vs employer-sourced ("Tìm ứng viên") — context for ops.
  source: applicationSourceSchema,
  applicationId: uuidSchema,
  workerId: uuidSchema,
  // Worker's profile name — admin sees it for every status (not masked).
  workerName: z.string().nullable(),
  workerAvatarUrl: z.string().nullable(),
  orgId: uuidSchema,
  orgName: z.string(),
  postingId: uuidSchema,
  postingTitle: z.string(),
  addressAlias: z.string().nullable(),
  district: z.string().nullable(),
  createdAt: isoDateTimeString, // invite issued at
  acceptedAt: isoDateTimeString.nullable(),
  committedAt: isoDateTimeString.nullable(),
  cancelledAt: isoDateTimeString.nullable(),
  expiresAt: isoDateTimeString,
  cancellationReason: z.string().nullable(),
  // Non-null only when status === COMMITTED (snapshot captured at commit).
  contact: inviteContactSchema.nullable(),
});

export const adminInviteListResponseSchema = pagedResponseSchema(
  adminInviteItemSchema,
);

// ============================================================================
// Shadow org create
// ============================================================================
export const adminCreateShadowOrgRequestSchema = z.object({
  name: z.string().min(1).max(120),
  businessType: z.string().min(1).max(60).default(DEFAULT_BUSINESS_TYPE),
  address: z.string().min(1).max(255).default("(chưa có)"),
  district: z.string().max(60).default("(chưa có)"),
  city: z.string().max(60).default("Hồ Chí Minh"),
  provinceCode: z.enum(PROVINCE_CODES).default("HCM"),
  lat: z.number().default(10.762622),
  lng: z.number().default(106.660172),
  // True when lat/lng are the placeholder fallback above (geocoding failed).
  geoApprox: z.boolean().default(false),
  mapUrl: z.string().url().nullable().optional(),
  contactName: z.string().min(1).max(120).default("(chưa có)"),
  contactPhone: phoneSchema.optional(),
  // Optional — where the auto claim-invite email is sent once the shadow org
  // receives its first applicant.
  contactEmail: z.string().email().optional(),
  defaultVenueType: z
    .enum([
      "RESTAURANT",
      "CAFE",
      "BAR_PUB",
      "HOTPOT_BBQ",
      "FAST_FOOD",
      "BAKERY",
    ])
    .default("RESTAURANT"),
});

// ============================================================================
// Cross-org postings (admin)
// ============================================================================
export const adminPostingListQuerySchema = pagePaginationQuerySchema.extend({
  state: postingStateSchema.optional(),
  orgId: uuidSchema.optional(),
  search: z.string().optional(),
});

export const adminPostingListItemSchema = postingSchema.extend({
  applicantCount: z.number().int(),
  newApplicantCount: z.number().int(),
});

export const adminPostingListResponseSchema = pagedResponseSchema(
  adminPostingListItemSchema,
);

// Admin posting detail = the public posting shape PLUS the scraped FB permalink
// (`sourceUrl`), which is admin-only and never returned to workers/employers.
export const adminPostingDetailSchema = postingSchema.extend({
  sourceUrl: z.string().nullable(),
});

// ============================================================================
// Claim invitations (handover shadow org → real employer)
// ============================================================================
export const orgClaimInvitationSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  email: z.string(),
  expiresAt: isoDateTimeString,
  claimedAt: isoDateTimeString.nullable(),
  revokedAt: isoDateTimeString.nullable(),
  createdAt: isoDateTimeString,
  // Only returned at creation time — once shown, the raw token is discarded.
  link: z.string().optional(),
});

export const orgClaimInvitationListResponseSchema = z.object({
  items: z.array(orgClaimInvitationSchema),
});

/** Admin global claim invitation row — includes minimal org info for table display. */
export const adminClaimInvitationItemSchema = orgClaimInvitationSchema.extend({
  orgName: z.string(),
  orgDistrict: z.string(),
  orgCity: z.string(),
});

export const adminClaimInvitationListQuerySchema =
  pagePaginationQuerySchema.extend({
    status: z.enum(["PENDING", "CLAIMED", "REVOKED", "EXPIRED"]).optional(),
  });

export const adminClaimInvitationListResponseSchema = pagedResponseSchema(
  adminClaimInvitationItemSchema,
);

export const adminCreateClaimInvitationRequestSchema = z.object({
  email: z.string().email(),
  ttlHours: z.number().int().min(1).max(720).default(168),
});

export const adminTodoCountsSchema = z.object({
  pendingWorkerKyc: z.number().int(),
  pendingOrgKyc: z.number().int(),
  openAbuseReports: z.number().int(),
  pendingClaimInvitations: z.number().int(),
  pendingCancellationRequests: z.number().int(),
  // Shadow (unclaimed) orgs that already have ≥1 pending applicant (state
  // APPLIED) and need an admin to act on the org's behalf. Drops to 0 once
  // every such org is either claimed (isShadow=false) or its applicants are
  // invited / rejected.
  shadowOrgsWithApplicants: z.number().int(),
});

// ============================================================================
// Shadow orgs with pending applicants (admin concierge todo)
// ============================================================================
// One row = a shadow org (isShadow=true, not deactivated) that has at least one
// posting application still in APPLIED state. Admin uses this to step in and
// schedule interviews on the org's behalf until the org is claimed.
export const adminShadowOrgWithApplicantsItemSchema = z.object({
  orgId: uuidSchema,
  name: z.string(),
  businessType: z.string(),
  district: z.string(),
  city: z.string(),
  // Total non-archived postings on the org (context for the admin).
  postingCount: z.number().int(),
  // Applications still in APPLIED state across all the org's postings.
  pendingApplicantCount: z.number().int(),
  // When the most recent still-pending application arrived. Drives sort order
  // (newest first) and the "applied at" column.
  latestAppliedAt: isoDateTimeString,
  // True when a claim invitation is already outstanding (pending) for this org,
  // so the admin knows whether the handover email has gone out.
  hasPendingClaimInvitation: z.boolean(),
});

export const adminShadowOrgsWithApplicantsQuerySchema =
  pagePaginationQuerySchema.extend({
    search: z.string().optional(),
  });

export const adminShadowOrgsWithApplicantsResponseSchema = pagedResponseSchema(
  adminShadowOrgWithApplicantsItemSchema,
);

// ============================================================================
// Grant a shadow org ("doanh nghiệp tạm") to an employer account (admin)
// ============================================================================
// Picker row for the "Trao doanh nghiệp" dialog: every unclaimed shadow org
// (isShadow=true, ownerId=null, not deactivated) an admin can hand to an
// employer account that owns none yet. Distinct from the concierge "shadow orgs
// with applicants" list — this is the full assignable pool, applicants or not.
export const adminAssignableShadowOrgItemSchema = z.object({
  orgId: uuidSchema,
  name: z.string(),
  businessType: z.string(),
  district: z.string(),
  city: z.string(),
  // Non-archived postings on the org — context so the admin knows it carries
  // real imported tin (and their applicants) over to the new owner.
  postingCount: z.number().int(),
});

export const adminAssignableShadowOrgsQuerySchema =
  pagePaginationQuerySchema.extend({
    search: z.string().optional(),
  });

export const adminAssignableShadowOrgListResponseSchema = pagedResponseSchema(
  adminAssignableShadowOrgItemSchema,
);

// Body = the chosen shadow org; the target employer account is the :userId path
// param. After granting: org.isShadow flips false, ownerId = userId, an OWNER
// membership is (re)created, and any pending claim-invite emails are revoked.
export const adminGrantOrgRequestSchema = z.object({
  orgId: uuidSchema,
});

export const adminGrantOrgResponseSchema = z.object({
  ok: z.literal(true),
  orgId: uuidSchema,
  orgName: z.string(),
});

// ============================================================================
// Public claim flow (no auth required)
// ============================================================================
// Posting-level applicant teaser shown on the claim landing page. Carries no
// PII — only the posting title, how many people applied, and when the most
// recent one arrived. `postingId` lets the landing page deep-link to the public
// posting so the prospective owner can preview the original ad.
export const claimPreviewPostingSchema = z.object({
  postingId: z.string(),
  title: z.string(),
  applicantCount: z.number().int().nonnegative(),
  latestAppliedAt: isoDateTimeString,
});
export type ClaimPreviewPosting = z.infer<typeof claimPreviewPostingSchema>;

export const claimPreviewResponseSchema = z.object({
  orgName: z.string(),
  orgDistrict: z.string(),
  orgCity: z.string(),
  email: z.string(),
  expiresAt: isoDateTimeString,
  // Proof-of-value teaser: how many open postings + applicants are waiting,
  // grouped per posting (with applicant counts) to render on the landing page.
  openPostingCount: z.number().int().nonnegative(),
  applicantCount: z.number().int().nonnegative(),
  postings: z.array(claimPreviewPostingSchema),
});

export const claimAcceptRequestSchema = z.object({
  password: z.string().min(8).max(200),
  fullName: z.string().min(1).max(120),
});

// ============================================================================
// User detail (admin)
// ============================================================================
export const adminApplicationSummarySchema = z.object({
  id: uuidSchema,
  postingId: uuidSchema,
  postingTitle: z.string(),
  orgId: uuidSchema,
  orgName: z.string(),
  state: z.string(),
  appliedAt: isoDateTimeString,
  decidedAt: isoDateTimeString.nullable(),
});

export const adminUserDetailSchema = userMeSchema.extend({
  accountType: accountTypeSchema,
  // Admin sees raw phone (incl. "pending-..." placeholder if applicable).
  rawPhone: z.string().nullable(),
  lastActiveAt: isoDateTimeString.nullable(),
  recentApplications: z.array(adminApplicationSummarySchema),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number().int(),
});

// ============================================================================
// Active sessions (admin view) — devices currently signed into an account.
// ============================================================================
export const adminSessionSchema = z.object({
  id: uuidSchema,
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  // Best-effort parsed UA for display; raw userAgent kept for ambiguous cases.
  deviceType: z.string(),
  os: z.string().nullable(),
  client: z.string().nullable(),
  lastSeenAt: isoDateTimeString,
  createdAt: isoDateTimeString,
  expiresAt: isoDateTimeString,
});

export const adminSessionListResponseSchema = z.object({
  items: z.array(adminSessionSchema),
});

export const adminRevokeSessionResponseSchema = z.object({
  revoked: z.number().int(),
});

// ============================================================================
// Credit pack pricing config (admin CRUD)
// ============================================================================
export const creditPackSchema = z.object({
  id: uuidSchema,
  packCode: z.string(),
  amountVnd: z.string(),
  creditCount: z.number().int(),
  unitPriceVnd: z.string(),
  originalPriceVnd: z.string().nullable(),
  effectiveFrom: isoDateTimeString,
  effectiveTo: isoDateTimeString.nullable(),
  note: z.string().nullable(),
  createdAt: isoDateTimeString,
});

export const creditPackListResponseSchema = z.object({
  items: z.array(creditPackSchema),
});

export const creditPackUpsertRequestSchema = z.object({
  packCode: z.string().min(1).max(40),
  amountVnd: z.union([z.number().int().nonnegative(), z.string()]),
  creditCount: z.number().int().min(1).max(100000),
  originalPriceVnd: z
    .union([z.number().int().nonnegative(), z.string()])
    .nullable()
    .optional(),
  effectiveFrom: isoDateTimeString.optional(),
  effectiveTo: isoDateTimeString.nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});

export const claimAcceptResponseSchema = z.object({
  ok: z.literal(true),
  orgId: uuidSchema,
  accessToken: z.string(),
  refreshToken: z.string(),
});

void phoneSchema;

export type AdminDashboardResponse = z.infer<
  typeof adminDashboardResponseSchema
>;
export type AdminAnalyticsRangeQuery = z.infer<
  typeof adminAnalyticsRangeQuerySchema
>;
export type AdminAnalyticsMetric = z.infer<typeof adminAnalyticsMetricSchema>;
export type AdminAnalyticsSummary = z.infer<typeof adminAnalyticsSummarySchema>;
export type AdminAnalyticsTimeseriesPoint = z.infer<
  typeof adminAnalyticsTimeseriesPointSchema
>;
export type AdminAnalyticsTimeseries = z.infer<
  typeof adminAnalyticsTimeseriesSchema
>;
export type AdminAnalyticsFunnel = z.infer<typeof adminAnalyticsFunnelSchema>;
export type AdminAnalyticsRevenue = z.infer<typeof adminAnalyticsRevenueSchema>;
export type AdminUserItem = z.infer<typeof adminUserItemSchema>;
export type AdminUsersQuery = z.infer<typeof adminUsersQuerySchema>;
export type AdminUsersListResponse = z.infer<
  typeof adminUsersListResponseSchema
>;
export type AdminSuspendUserRequest = z.infer<
  typeof adminSuspendUserRequestSchema
>;
export type AdminUserMutationResponse = z.infer<
  typeof adminUserMutationResponseSchema
>;
export type AdminUserContactResponse = z.infer<
  typeof adminUserContactResponseSchema
>;
export type AdminKycListQuery = z.infer<typeof adminKycListQuerySchema>;
export type AdminWorkerKycListResponse = z.infer<
  typeof adminWorkerKycListResponseSchema
>;
export type AdminOrgKycListResponse = z.infer<
  typeof adminOrgKycListResponseSchema
>;
export type AdminOrgListQuery = z.infer<typeof adminOrgListQuerySchema>;
export type AdminOrgListItem = z.infer<typeof adminOrgListItemSchema>;
export type AdminOrgListResponse = z.infer<typeof adminOrgListResponseSchema>;
export type AdminCreditPurchaseListQuery = z.infer<
  typeof adminCreditPurchaseListQuerySchema
>;
export type AdminCreditPurchaseItem = z.infer<
  typeof adminCreditPurchaseItemSchema
>;
export type AdminCreditPurchaseListResponse = z.infer<
  typeof adminCreditPurchaseListResponseSchema
>;
export type AdminCreditPurchaseSummaryQuery = z.infer<
  typeof adminCreditPurchaseSummaryQuerySchema
>;
export type AdminCreditPurchaseSummary = z.infer<
  typeof adminCreditPurchaseSummarySchema
>;
export type AdminCreditPurchaseDetail = z.infer<
  typeof adminCreditPurchaseDetailSchema
>;
export type AdminCreditPurchaseMarkPaidRequest = z.infer<
  typeof adminCreditPurchaseMarkPaidRequestSchema
>;
export type AdminCreditPurchaseActionResponse = z.infer<
  typeof adminCreditPurchaseActionResponseSchema
>;
export type AdminInviteListQuery = z.infer<typeof adminInviteListQuerySchema>;
export type AdminInviteItem = z.infer<typeof adminInviteItemSchema>;
export type AdminInviteListResponse = z.infer<
  typeof adminInviteListResponseSchema
>;
export type AdminCreateShadowOrgRequest = z.infer<
  typeof adminCreateShadowOrgRequestSchema
>;
export type AdminPostingListQuery = z.infer<typeof adminPostingListQuerySchema>;
export type AdminPostingListItem = z.infer<typeof adminPostingListItemSchema>;
export type AdminPostingDetail = z.infer<typeof adminPostingDetailSchema>;
export type AdminPostingListResponse = z.infer<
  typeof adminPostingListResponseSchema
>;
export type OrgClaimInvitation = z.infer<typeof orgClaimInvitationSchema>;
export type OrgClaimInvitationListResponse = z.infer<
  typeof orgClaimInvitationListResponseSchema
>;
export type AdminCreateClaimInvitationRequest = z.infer<
  typeof adminCreateClaimInvitationRequestSchema
>;
export type AdminClaimInvitationItem = z.infer<
  typeof adminClaimInvitationItemSchema
>;
export type AdminClaimInvitationListQuery = z.infer<
  typeof adminClaimInvitationListQuerySchema
>;
export type AdminClaimInvitationListResponse = z.infer<
  typeof adminClaimInvitationListResponseSchema
>;
export type AdminTodoCounts = z.infer<typeof adminTodoCountsSchema>;
export type AdminShadowOrgWithApplicantsItem = z.infer<
  typeof adminShadowOrgWithApplicantsItemSchema
>;
export type AdminShadowOrgsWithApplicantsQuery = z.infer<
  typeof adminShadowOrgsWithApplicantsQuerySchema
>;
export type AdminShadowOrgsWithApplicantsResponse = z.infer<
  typeof adminShadowOrgsWithApplicantsResponseSchema
>;
export type AdminAssignableShadowOrgItem = z.infer<
  typeof adminAssignableShadowOrgItemSchema
>;
export type AdminAssignableShadowOrgsQuery = z.infer<
  typeof adminAssignableShadowOrgsQuerySchema
>;
export type AdminAssignableShadowOrgListResponse = z.infer<
  typeof adminAssignableShadowOrgListResponseSchema
>;
export type AdminGrantOrgRequest = z.infer<typeof adminGrantOrgRequestSchema>;
export type AdminGrantOrgResponse = z.infer<typeof adminGrantOrgResponseSchema>;
export type ClaimPreviewResponse = z.infer<typeof claimPreviewResponseSchema>;
export type ClaimAcceptRequest = z.infer<typeof claimAcceptRequestSchema>;
export type ClaimAcceptResponse = z.infer<typeof claimAcceptResponseSchema>;
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;
export type AdminSession = z.infer<typeof adminSessionSchema>;
export type AdminSessionListResponse = z.infer<
  typeof adminSessionListResponseSchema
>;
export type AdminRevokeSessionResponse = z.infer<
  typeof adminRevokeSessionResponseSchema
>;
export type AdminApplicationSummary = z.infer<
  typeof adminApplicationSummarySchema
>;
export type CreditPack = z.infer<typeof creditPackSchema>;
export type CreditPackListResponse = z.infer<
  typeof creditPackListResponseSchema
>;
export type CreditPackUpsertRequest = z.infer<
  typeof creditPackUpsertRequestSchema
>;
